// Display helpers shared by the customers list, its side panel and the
// customer profile. Responses are read raw (snake_case).
import { formatNumber } from "../dashboard/format";

// What a customer's `point` counts, by card type (passes.pass.type):
// stamp cards count stamps; prepaid, cashback-money and discount cards hold
// money in the store's currency (a discount card's balance is what the
// customer saved, TransactionService#process_discount_campaign); every other
// type (point "cashback", membership, coupon) shows the number as points.
const MONEY_TYPES = new Set(["balance", "cashback_money", "discount"]);

export function balanceUnit(passType) {
  if (passType === "stamp") return "stamps";
  if (MONEY_TYPES.has(passType)) return "money";
  return "points";
}

// The list's summary pass carries `type`; the profile's full pass carries
// the design blob it comes from.
export function passTypeOf(pass) {
  return pass?.type ?? pass?.pass?.type ?? null;
}

const round2 = (value) => Math.round(Number(value) * 100) / 100;

function formatAmount(value, unit, language) {
  return formatNumber(Math.abs(round2(value)), language, {
    maximumFractionDigits: 2,
    minimumFractionDigits: unit === "money" && !Number.isInteger(round2(value)) ? 2 : 0,
  });
}

// "4 stamps", "310 pts", "€ 12.50". `currency` is stores.currency, a symbol
// stored verbatim and printed in front of amounts (as on the wallet card).
export function formatBalance(value, unit, { t, language, currency }) {
  const number = round2(Number(value) || 0);
  const text = formatAmount(number, unit, language);
  const sign = number < 0 ? "−" : "";
  if (unit === "money") return `${sign}${currency ? `${currency} ` : ""}${text}`;
  return `${sign}${t(`customers.units.${unit}.value`, { value: text, count: Math.abs(number) })}`;
}

// The balance as a bare figure, for a stat whose label already names the
// unit ("Stamps 4", not "Stamps 4 stamps"); money keeps its currency.
export function formatBalanceFigure(value, unit, { language, currency }) {
  const number = round2(Number(value) || 0);
  const text = formatAmount(number, unit, language);
  const sign = number < 0 ? "−" : "";
  if (unit === "money") return `${sign}${currency ? `${currency} ` : ""}${text}`;
  return `${sign}${text}`;
}

// A transaction's change: "+1", "−10", "+€ 5".
export function formatDelta(value, unit, { language, currency }) {
  const number = round2(Number(value) || 0);
  const text = formatAmount(number, unit, language);
  const sign = number > 0 ? "+" : number < 0 ? "−" : "";
  if (unit === "money") return `${sign}${currency ? `${currency} ` : ""}${text}`;
  return `${sign}${text}`;
}

// Transaction kinds the API sends (Transaction#kind). Anything else gets the
// generic label rather than a raw enum name.
export const TRANSACTION_KINDS = [
  "initial",
  "scan",
  "manual",
  "redeem",
  "referrer",
  "referee",
  "birthday",
  "upgrade",
  "bookingkit",
  "shopify",
  "expired",
];

export function kindLabelKey(kind) {
  return TRANSACTION_KINDS.includes(kind)
    ? `customers.kinds.${kind}`
    : "customers.kinds.other";
}

const timeOf = (tx) => {
  const time = new Date(tx?.created_at).getTime();
  return Number.isNaN(time) ? 0 : time;
};

// The customer's transactions oldest first, each with `balance_after`: the
// running sum of `point`. customers.point is rewritten as SUM(transactions.
// point) after every transaction, so the last row's balance equals the
// customer's `point`. The API already sends them in ascending order; the sort
// only guards against a reorder, with the id breaking same-time ties.
export function withBalanceAfter(transactions) {
  const ordered = [...(transactions || [])].sort(
    (a, b) => timeOf(a) - timeOf(b) || (a.id ?? 0) - (b.id ?? 0),
  );
  let balance = 0;
  return ordered.map((tx) => {
    balance = round2(balance + (Number(tx.point) || 0));
    return { ...tx, balance_after: balance };
  });
}

// Visits and last visit by the same definition as the list's last_visit_at
// and the health segments (the customer's `scan` transactions); rewards are
// the `redeem` ones.
export function visitStats(transactions) {
  let visits = 0;
  let rewards = 0;
  let last = null;
  for (const tx of transactions || []) {
    if (tx.kind === "scan") {
      visits += 1;
      if (!last || timeOf(tx) > timeOf(last)) last = tx;
    } else if (tx.kind === "redeem") {
      rewards += 1;
    }
  }
  return { visits, rewards, lastVisitAt: last?.created_at ?? null };
}

const DAY_MS = 86400000;

// The health segment a last visit falls in, with the backend's boundaries
// (CustomerSegments.condition): an exact 30/60/90 days belongs to the older
// segment. Used only to label a row; the segment filter itself is the API's.
export function segmentForLastVisit(lastVisitAt, now = new Date()) {
  if (!lastVisitAt) return "never";
  const time = new Date(lastVisitAt).getTime();
  if (Number.isNaN(time)) return "never";
  const age = now.getTime() - time;
  if (age < 30 * DAY_MS) return "active";
  if (age < 60 * DAY_MS) return "cooling";
  if (age < 90 * DAY_MS) return "at_risk";
  return "lost";
}

// "1990-05-01" is a calendar day; build it from its parts so it does not
// shift a day west of Greenwich.
export function formatCalendarDay(value, language, options = { day: "numeric", month: "short", year: "numeric" }) {
  if (!value) return null;
  const [y, m, d] = String(value).slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  try {
    return new Intl.DateTimeFormat(language, options).format(new Date(y, m - 1, d));
  } catch {
    return new Intl.DateTimeFormat("en", options).format(new Date(y, m - 1, d));
  }
}

export function formatDate(iso, language, options = { day: "numeric", month: "short", year: "numeric" }) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  try {
    return new Intl.DateTimeFormat(language, options).format(date);
  } catch {
    return new Intl.DateTimeFormat("en", options).format(date);
  }
}

export function formatDateTime(iso, language) {
  return formatDate(iso, language, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function phoneText(customer) {
  if (!customer?.phone) return null;
  return [customer.phone_country_code, customer.phone].filter(Boolean).join(" ");
}

// Editing a customer is blocked once a store has neither a live subscription
// nor a running trial; the page shows TrialEndedAlert instead.
export function isEditBlocked(store, now = new Date()) {
  const status = store?.subscription_status;
  if (status === "active" || status === "past_due" || status === "trialing") {
    return false;
  }
  if (store?.trial_expires_at && now < new Date(store.trial_expires_at)) {
    return false;
  }
  return true;
}

// The list asks for 25 rows a page.
export const PER_PAGE = 25;

// Up to five page numbers around the current one.
export function pageWindow(current, total) {
  let start = Math.max(1, current - 2);
  const end = Math.min(total, start + 4);
  start = Math.max(1, end - 4);
  const pages = [];
  for (let p = start; p <= end; p++) pages.push(p);
  return pages;
}
